/**
 * Import — the front door.
 *
 * See docs/decisions/product-position.md. We do not beat Procreate at drawing,
 * so the tool wraps around it: art comes in from wherever it was made, and what
 * we add is everything a paint program structurally cannot know — that this file
 * is 585x559 for a reason, that those rectangles are a torso, that the 54.9% in
 * the middle never renders.
 *
 * Two ways in:
 *
 *   importFile()   one-shot, from drag-drop or a picker. Works everywhere.
 *   linkLiveFile() holds a handle to the file on disk and re-reads it when the
 *                  artist saves. Chromium desktop only, and the whole point.
 */

import type { RegionId } from "../../shared/format";
import { CANVAS } from "../../shared/format";
import { GUTTER_PX, REGIONS } from "../../shared/regions";

/** Anything wider than this is almost certainly not a garment template. */
const MAX_SOURCE_PX = 8192;

const TARGET_ASPECT = CANVAS.width / CANVAS.height;
/** ±1% covers integer-rounded exports (1170x1118, 2340x2236) without letting
 *  a genuinely wrong aspect through and silently squashing someone's art. */
const ASPECT_TOLERANCE = 0.01;

export type ImportFit =
  /** Already 585x559. Drawn 1:1, byte-for-byte where the artist put it. */
  | "exact"
  /** Right shape, wrong size — a 2x or 4x export. Scaled to fit exactly. */
  | "rescale"
  /** Wrong shape. Letterboxed and centred, because squashing is worse. */
  | "contain";

export interface RegionCoverage {
  region: RegionId;
  /** 0–1. How much of the region has any opacity at all. */
  covered: number;
}

export interface ImportReport {
  fit: ImportFit;
  sourceWidth: number;
  sourceHeight: number;
  /** Opaque pixels that landed where Roblox never renders. Wasted work. */
  deadSpacePixels: number;
  /** Regions with essentially nothing in them — likely an unfinished import. */
  emptyRegions: RegionId[];
  coverage: RegionCoverage[];
  notes: string[];
}

export interface ImportResult {
  bitmap: ImageBitmap;
  report: ImportReport;
}

function classify(w: number, h: number): { fit: ImportFit; note?: string } {
  if (w === CANVAS.width && h === CANVAS.height) return { fit: "exact" };

  const aspect = w / h;
  if (Math.abs(aspect - TARGET_ASPECT) / TARGET_ASPECT <= ASPECT_TOLERANCE) {
    const scale = w / CANVAS.width;
    return {
      fit: "rescale",
      note:
        scale > 1
          ? `Scaled down from ${w}x${h} (${scale.toFixed(2)}x export).`
          : `Scaled up from ${w}x${h} — detail will be soft.`,
    };
  }

  return {
    fit: "contain",
    note:
      `${w}x${h} is not a ${CANVAS.width}x${CANVAS.height} template shape. ` +
      `Centred without stretching — check it lines up with the guide.`,
  };
}

/**
 * Decode a file to a bitmap sized for the canvas.
 *
 * createImageBitmap does the resampling on the browser's own scaler rather than
 * a drawImage round trip, which matters for the 4x Procreate exports where a
 * naive downscale aliases the pixel-art crowd's work into mush.
 */
async function decode(file: File, fit: ImportFit): Promise<ImageBitmap> {
  if (fit === "rescale") {
    return createImageBitmap(file, {
      resizeWidth: CANVAS.width,
      resizeHeight: CANVAS.height,
      resizeQuality: "high",
    });
  }
  return createImageBitmap(file);
}

/**
 * What we know that Photoshop does not.
 *
 * Reads the imported pixels against the measured region map: how much of each
 * face has art on it, and how much of the file was spent on the dead space that
 * Roblox throws away. The second number is the useful one — a large count means
 * the artist used the wrong template or drew outside the net, and they would
 * otherwise find out after paying to upload.
 *
 * Exported because it is the only part of this file worth testing and the only
 * part that does not need a browser — everything else is decode plumbing.
 */
export function analyseCoverage(
  data: Uint8ClampedArray,
  width: number,
  height: number,
): {
  deadSpacePixels: number;
  coverage: RegionCoverage[];
  emptyRegions: RegionId[];
} {
  // Mark every pixel that belongs to a region (plus its gutter, which is
  // legitimately paintable because dilation will consume it).
  const owner = new Int8Array(width * height);
  const ids = Object.keys(REGIONS) as RegionId[];
  const hits = new Uint32Array(ids.length);
  const areas = new Uint32Array(ids.length);

  ids.forEach((id, i) => {
    const r = REGIONS[id];
    const x0 = Math.max(0, r.x - GUTTER_PX);
    const y0 = Math.max(0, r.y - GUTTER_PX);
    const x1 = Math.min(width, r.x + r.w + GUTTER_PX);
    const y1 = Math.min(height, r.y + r.h + GUTTER_PX);
    areas[i] = r.w * r.h;
    for (let y = y0; y < y1; y++) {
      const row = y * width;
      for (let x = x0; x < x1; x++) owner[row + x] = i + 1;
    }
  });

  let deadSpacePixels = 0;
  for (let p = 0; p < owner.length; p++) {
    if (!data[p * 4 + 3]) continue; // transparent — nothing there
    const o = owner[p];
    if (o) {
      const idx = o - 1;
      // biome-ignore lint/style/noNonNullAssertion: index is in range by construction
      hits[idx]! += 1;
    } else {
      deadSpacePixels++;
    }
  }

  const coverage: RegionCoverage[] = ids.map((region, i) => ({
    region,
    covered: areas[i] ? Math.min(1, (hits[i] ?? 0) / (areas[i] ?? 1)) : 0,
  }));
  // 2% is the threshold for "there is something here" rather than a stray
  // antialiased pixel from a stroke that clipped the corner.
  const emptyRegions = coverage.filter((c) => c.covered < 0.02).map((c) => c.region);

  return { deadSpacePixels, coverage, emptyRegions };
}

/** Decode, place and report. Does not touch the paint surface — the caller
 *  decides whether to accept, which is what makes a preview possible later. */
export async function importFile(file: File): Promise<ImportResult> {
  if (!file.type.startsWith("image/")) {
    throw new Error(`${file.name} is not an image.`);
  }

  const probe = await createImageBitmap(file);
  // close() zeroes width and height, so the source dimensions have to be read
  // out now — reading them off `probe` at the end reported every rescaled
  // import as 0x0, which is the one number the artist is checking.
  const sourceWidth = probe.width;
  const sourceHeight = probe.height;

  if (sourceWidth > MAX_SOURCE_PX || sourceHeight > MAX_SOURCE_PX) {
    probe.close();
    throw new Error(`${sourceWidth}x${sourceHeight} is too large to be a template.`);
  }

  const { fit, note } = classify(sourceWidth, sourceHeight);
  const bitmap = fit === "rescale" ? await decode(file, fit) : probe;
  if (bitmap !== probe) probe.close();

  // Compose onto a template-sized canvas so the analysis runs on exactly the
  // pixels that would be exported, not on the source.
  const flat = document.createElement("canvas");
  flat.width = CANVAS.width;
  flat.height = CANVAS.height;
  const ctx = flat.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("2D context unavailable");

  if (fit === "contain") {
    const s = Math.min(CANVAS.width / bitmap.width, CANVAS.height / bitmap.height);
    const w = bitmap.width * s;
    const h = bitmap.height * s;
    ctx.drawImage(bitmap, (CANVAS.width - w) / 2, (CANVAS.height - h) / 2, w, h);
  } else {
    ctx.drawImage(bitmap, 0, 0);
  }

  const pixels = ctx.getImageData(0, 0, CANVAS.width, CANVAS.height);
  const { deadSpacePixels, coverage, emptyRegions } = analyseCoverage(
    pixels.data,
    CANVAS.width,
    CANVAS.height,
  );

  const notes: string[] = [];
  if (note) notes.push(note);
  if (deadSpacePixels > 2000) {
    notes.push(`${deadSpacePixels.toLocaleString()} px land outside the net and will not render.`);
  }

  // Hand back a bitmap of the composed result rather than the source, so the
  // caller draws the same thing that was measured.
  const placed = await createImageBitmap(flat);
  bitmap.close();

  return {
    bitmap: placed,
    report: {
      fit,
      sourceWidth,
      sourceHeight,
      deadSpacePixels,
      emptyRegions,
      coverage,
      notes,
    },
  };
}

// ------------------------------------------------------------- live linking

/**
 * The File System Access API, declared narrowly.
 *
 * TypeScript's DOM lib does not ship these, and pulling in a whole ambient
 * package for two methods is worse than eight lines. Everything is optional
 * because Safari and Firefox do not implement any of it.
 */
interface FileHandle {
  readonly name: string;
  getFile(): Promise<File>;
}
declare global {
  interface Window {
    showOpenFilePicker?: (opts?: {
      multiple?: boolean;
      types?: { description: string; accept: Record<string, string[]> }[];
    }) => Promise<FileHandle[]>;
  }
}

export interface LiveLink {
  filename: string;
  stop(): void;
}

export function liveLinkSupported(): boolean {
  return typeof window.showOpenFilePicker === "function";
}

/** How often to stat the file. Fast enough to feel instant after ⌘S, slow
 *  enough that it costs nothing — this is a metadata read, not a decode. */
const POLL_MS = 700;

/**
 * Keep a file on disk and the 3D body in sync.
 *
 * This is the feature the desktop paint programs cannot have, because it is not
 * about drawing: the artist stays in Photoshop or Clip Studio on one screen,
 * hits save, and watches the avatar update on the other. No download, no
 * re-upload, no drag-and-drop, no leaving their tool.
 *
 * Returns null if the user dismisses the picker, which is a normal outcome and
 * not an error.
 */
export async function linkLiveFile(
  onChange: (result: ImportResult, filename: string) => void,
  onError?: (message: string) => void,
): Promise<LiveLink | null> {
  const pick = window.showOpenFilePicker;
  if (!pick) throw new Error("This browser cannot watch files. Drag the PNG in instead.");

  let handles: FileHandle[];
  try {
    handles = await pick({
      multiple: false,
      types: [{ description: "Roblox template", accept: { "image/png": [".png"] } }],
    });
  } catch {
    return null; // dismissed
  }

  const handle = handles[0];
  if (!handle) return null;

  let lastModified = -1;
  let stopped = false;
  let inFlight = false;

  const tick = async () => {
    if (stopped || inFlight) return;
    try {
      const file = await handle.getFile();
      if (file.lastModified === lastModified) return;
      lastModified = file.lastModified;
      inFlight = true;
      // The first tick runs before linkLiveFile returns, so the caller has no
      // handle yet — pass the name rather than making them find it.
      onChange(await importFile(file), handle.name);
    } catch (err) {
      // The file was deleted, moved, or permission lapsed. Report once and keep
      // polling — an artist doing Save As will trip this and then recover.
      onError?.(err instanceof Error ? err.message : "Could not re-read the file.");
    } finally {
      inFlight = false;
    }
  };

  void tick();
  const timer = window.setInterval(tick, POLL_MS);

  return {
    filename: handle.name,
    stop() {
      stopped = true;
      window.clearInterval(timer);
    },
  };
}

/**
 * Drag-and-drop, everywhere.
 *
 * The fallback for the two-thirds of browsers without file handles, and still a
 * two-second loop. Both dragover and dragenter must preventDefault or the
 * browser navigates away to the image and the user loses their work.
 */
export function wireDropTarget(
  el: HTMLElement,
  onFile: (file: File) => void,
  onHover?: (over: boolean) => void,
) {
  let depth = 0;

  const over = (e: DragEvent) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  el.addEventListener("dragenter", (e) => {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    depth++;
    onHover?.(true);
  });
  el.addEventListener("dragover", over);
  el.addEventListener("dragleave", () => {
    // dragleave fires for every child crossed, so count rather than toggle.
    depth = Math.max(0, depth - 1);
    if (depth === 0) onHover?.(false);
  });
  el.addEventListener("drop", (e) => {
    e.preventDefault();
    depth = 0;
    onHover?.(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  });
}
